

import React, { useState, useEffect, type ReactNode, SyntheticEvent, KeyboardEvent, ChangeEvent } from 'react';
import cn from 'classnames';
import styled from 'styled-components';
import styles from './input.module.css';
import { FormLabelWrapper, Label } from './Label';
import { testEmailAgainstRegex } from '../../lib/airframe/FormValidation';

export type InputProps = {
  allowEmptyError?: boolean,
  autoFocus?: boolean,
  className?: string,
  error?: boolean,
  id?: string,
  name?: string,
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void,
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void,
  placeholder?: string,
  type?: string,
  value?: string | number,
  disabled?: boolean,
  maxLength?: number,
  autocomplete?: string,
};

// Goes around entire form to make rows of inputs
export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FormInputWrapper = styled.div`
  margin-left: 0.625rem;
  width: ${(props: { width?: any; }) => props.width ?? '15.625rem'};
`;

const FormLabeledInputWrapper = styled.div`
  display: inline-flex;
  flex-direction: row;
  margin: 0.3rem;
`;

type RowWrapperProps = {
  elementId: string,
  label: string,
  children: ReactNode,
  width?: string,
  inputWidth?: string,
};

// A row containing a label, then an element of any type
export const FormLabeledInputRowWrapper: React.FC<RowWrapperProps> = ({
  children,
  label,
  elementId,
  width,
  inputWidth,
}) => (
    <FormLabeledInputWrapper>
      <FormLabelWrapper htmlFor={elementId} text={label} width={width} />
      <FormInputWrapper width={inputWidth}>{children}</FormInputWrapper>
    </FormLabeledInputWrapper>
  );

const StyledLabel = styled(Label)`
  display: flex;
`;

export const FilterabeledInputWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  padding: 0.3rem;
  width: ${(props: { width: any; }) => props.width ?? 'max-content'};
`;

// Filter inputs: label above an input
export const FilterLabeledInputRowWrapper: React.FC<RowWrapperProps> = ({
  children,
  label,
  elementId,
  width,
}) => {
  return (
    <FilterabeledInputWrapper width={width}>
      <StyledLabel htmlFor={elementId} text={label} />
      {children}
    </FilterabeledInputWrapper>
  );
};

const BaseInput: React.FC<InputProps> = ({
  allowEmptyError = false,
  autoFocus,
  className,
  error,
  id,
  name,
  onChange,
  onKeyPress,
  placeholder,
  type,
  value,
  disabled = false,
  maxLength,
}) => {
  const valueIsEmpty = !(value ?? null);
  const showError = allowEmptyError ? error : !valueIsEmpty && error;
  return (
    <input
      autoFocus={autoFocus}
      className={cn(
        'form-control',
        showError ? styles.error : '',
        className,
        disabled ? styles.disabled : '',
      )}
      id={id || name}
      name={name}
      onChange={onChange}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      type={type}
      value={value}
      disabled={disabled}
      maxLength={maxLength}
    />
  );
};

export const Input: React.FC<InputProps> = (props) => (
  <BaseInput
    // adding cn in here for now to pass through any passed classnames for compatibility w/ styled components
    //  !!! it's double spread so the calculated classname overwrites the passed classname
    {...{ ...props, className: cn(styles.inputPrimary, props.className) }}
  />
);

Input.defaultProps = {
  type: 'text',
  placeholder: '',
  value: '',
  onKeyPress: () => {},
  className: '',
  error: false,
};
export type EmailInputProps = InputProps & {
  value?: string | null,
  onError?: (errorMessage?: string) => void,
};

export const EmailInput: React.FC<EmailInputProps> = (props) => {
  const [hasError, updateHasError] = useState(props.error);

  const errorMessages = {
    invalid: 'Not a valid email',
  };

  useEffect(() => {
    updateHasError(props.error);
  }, [props.error]);

  useEffect(() => {
    if (props.value) {
      const isValidEmail = isEmailValid(props.value);
      updateHasError(!isValidEmail);
    }
  }, [props.value]);

  const handleOnError = (error: boolean) => {
    if (props.onError) {
      error ? props.onError(errorMessages.invalid) : props.onError(undefined);
    }
  };

  const isEmailValid = (value: string) => {
    if (value) {
      return testEmailAgainstRegex(value);
    }
    return true;
  };

  return (
    <BaseInput
      {...{
        ...props,
        error: hasError,
        className: styles.inputPrimary,
        onChange: (e: ChangeEvent<HTMLInputElement>): void => {
          const value = e.currentTarget.value;
          const isValidEmail = isEmailValid(value);

          updateHasError(!isValidEmail);
          handleOnError(!isValidEmail);
          if (props.onChange) {
            props.onChange(e);
          }
        },
      }}
    />
  );
};

EmailInput.defaultProps = {
  type: 'email',
  placeholder: '',
  value: '',
  onKeyPress: () => {},
  className: '',
  error: false,
};

export const ModalInput: React.FC<InputProps> = (props) => <BaseInput {...props} />;

ModalInput.defaultProps = {
  type: 'text',
  placeholder: '',
  value: '',
  onKeyPress: () => {},
  className: '',
  error: false,
};

export type NumericInputProps = {
  placeholder?: string,
  name: string,
  value?: number,
  onChange: (event: SyntheticEvent<HTMLInputElement>) => void,
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void,
  className?: string,
};

export const NumericInput: React.FC<NumericInputProps> = (props) => (
  <input
    type="number"
    className={cn('form-control', props.className)}
    placeholder={props.placeholder}
    name={props.name}
    value={props.value === undefined ? '' : props.value}
    onChange={props.onChange}
    onKeyPress={props.onKeyPress}
  />
);

NumericInput.defaultProps = {
  placeholder: '',
  onKeyPress: () => {},
  className: '',
};

export default Input;
